// =============================================================================
// Hosted-agent scheduled routines (B1 — HERMES wave 2)
//
// A routine is a scheduled instruction stored on the agent document
// (`routines[]`). The definitions themselves are edited in the wizard's
// Routines tab and persisted through the ordinary revision-guarded PUT
// (toHostedAgentUpdatePayload carries them). Two auxiliary ops endpoints
// exist for the things the form cannot do:
//   - POST .../routines/<routineId>/run   → enqueue one run now (initiator "schedule")
//   - GET  .../routines/<routineId>/runs  → recent run-posts for one routine
// Scheduled M365 calls require the owner's explicit background connection.
// =============================================================================

import { api } from "@/lib/api-client"

/**
 * A scheduled routine on the agent document (`routines[]`). Exactly one of
 * `cron` (5-field crontab) / `interval_minutes` (>= 1) is set; the other is
 * null. `trigger` is "schedule" in this wave ("event"/"agent" land later).
 */
export type HostedAgentRoutineDelivery = {
  channels: ("email" | "teams")[]
  email_recipients: string[]
  teams_destination_ids: string[]
  include_content: boolean
  audience: "private" | "agent_audience"
}

export type HostedAgentRoutine = {
  id: string
  name: string
  enabled: boolean
  trigger: "schedule"
  cron: string | null
  interval_minutes: number | null
  timezone: string
  prompt: string
  use_owner_m365_connection?: boolean
  delivery?: HostedAgentRoutineDelivery
  created_at?: string | null
  updated_at?: string | null
  created_by?: string | null
}

/**
 * The run ledger's vocabulary (`services/hosted_agents/runs.py`). The Run
 * history badge set (queued/succeeded/cancelled) is a UI mapping kept in
 * `features/admin-dashboard/utils/hosted-agent-routine-runs.ts`.
 */
export type HostedAgentRoutineRunStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "interrupted"

/**
 * A non-fatal note on a run that still executed, e.g.
 * `routine_owner_groups_unresolved` (the Graph group lookup failed, so the run
 * executed without the owner's groups).
 */
export type HostedAgentRoutineRunWarning = {
  code: string
  message: string
}

/** One durable routine run-post (`initiator: "schedule"`), newest first. */
export type HostedAgentRoutineRun = {
  run_id: string
  routine_id: string | null
  status: HostedAgentRoutineRunStatus | string
  /** When the run was queued. */
  created_at: string | null
  /** When a worker picked the run up. */
  claimed_at: string | null
  finished_at: string | null
  duration_ms: number | null
  error: { code: string; message: string } | null
  warnings?: HostedAgentRoutineRunWarning[]
  /** Legacy field name for the run timestamp; read only as a fallback. */
  started_at?: string | null
  /** Legacy flat failure code; read only as a fallback for `error.code`. */
  error_code?: string | null
}

export type HostedAgentRoutineRunNowResult = {
  success: boolean
  run_id: string | null
  routine_id: string
  status: HostedAgentRoutineRunStatus | string
  warnings?: HostedAgentRoutineRunWarning[]
}

/**
 * Recent routine runs for an agent (durable run-posts filtered to
 * `initiator: "schedule"`). Pass `routineId` to scope to one routine; omit
 * for every routine on the agent. Returns [] when the run service is not
 * wired ("cannot say" degrades to empty — the panel shows an empty state).
 */
export async function listHostedAgentRoutineRuns(
  agentId: string,
  routineId?: string
): Promise<HostedAgentRoutineRun[]> {
  // The backend serves run history per routine at
  // `.../routines/<routineId>/runs`; there is no agent-wide `/routines/runs`
  // collection. Calling that shape returned 404 on every poll, which the
  // panel rendered as "Körhistoriken är inte tillgänglig just nu" even when
  // the run service was perfectly healthy. Without a routine there is nothing
  // to ask for, so answer the empty history rather than a URL that 404s.
  if (!routineId) return []
  const response = await api.get(
    `/admin/hosted-agents/${encodeURIComponent(agentId)}/routines/${encodeURIComponent(routineId)}/runs`
  )
  const data = response.data || response
  return Array.isArray(data?.runs) ? (data.runs as HostedAgentRoutineRun[]) : []
}

/**
 * Enqueue one run of a routine immediately (admin "Run now"). The run goes
 * through the same durable turn path as the scheduler with
 * `initiator: "schedule"`; the response carries the queued run id.
 */
export async function runHostedAgentRoutineNow(
  agentId: string,
  routineId: string
): Promise<HostedAgentRoutineRunNowResult> {
  const response = await api.post(
    `/admin/hosted-agents/${encodeURIComponent(agentId)}/routines/${encodeURIComponent(
      routineId
    )}/run`
  )
  const data = response.data || response
  if (data.success === false) {
    throw new Error(data.message || data.error || "Failed to start the routine run")
  }
  return data
}
